import apiClient from './client'
import type { PaginatedResponse } from '@/types'

// Types
export interface NewsletterTemplate {
  id: number
  name: string
  subject_template: string
  html_template: string
  text_template: string
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface NewsletterCampaign {
  id: number
  name: string
  subject: string
  content_html: string
  content_text: string
  template: number | null
  template_name?: string
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'cancelled'
  target_roles: string[] | null
  scheduled_at: string | null
  sent_at: string | null
  recipients_count: number
  sent_count: number
  opened_count: number
  clicked_count: number
  bounced_count: number
  unsubscribed_count: number
  open_rate: number
  click_rate: number
  created_by: number
  created_by_name?: string
  created_at: string
  updated_at: string
}

export interface NewsletterRecipient {
  id: number
  campaign: number
  user: number
  user_name: string
  email: string
  status: 'pending' | 'sent' | 'delivered' | 'opened' | 'clicked' | 'bounced' | 'failed'
  sent_at: string | null
  opened_at: string | null
  clicked_at: string | null
  error_message: string
}

export interface ScheduledDigest {
  id: number
  name: string
  frequency: 'daily' | 'weekly' | 'monthly'
  is_active: boolean
  include_forum_highlights: boolean
  include_new_courses: boolean
  include_upcoming_events: boolean
  include_top_documents: boolean
  include_leaderboard: boolean
  send_day: number
  send_hour: number
  last_sent_at: string | null
  next_send_at: string | null
  created_at: string
  updated_at: string
}

export interface CampaignStats {
  total_campaigns: number
  sent_campaigns: number
  total_recipients: number
  total_opens: number
  total_clicks: number
  avg_open_rate: number
  avg_click_rate: number
}

export const newsletterApi = {
  // Templates
  getTemplates: async (): Promise<NewsletterTemplate[]> => {
    const response = await apiClient.get('/newsletter/templates/')
    return response.data.results || response.data
  },

  getTemplate: async (id: number): Promise<NewsletterTemplate> => {
    const response = await apiClient.get(`/newsletter/templates/${id}/`)
    return response.data
  },

  createTemplate: async (data: Partial<NewsletterTemplate>): Promise<NewsletterTemplate> => {
    const response = await apiClient.post('/newsletter/templates/', data)
    return response.data
  },

  updateTemplate: async (id: number, data: Partial<NewsletterTemplate>): Promise<NewsletterTemplate> => {
    const response = await apiClient.patch(`/newsletter/templates/${id}/`, data)
    return response.data
  },

  deleteTemplate: async (id: number): Promise<void> => {
    await apiClient.delete(`/newsletter/templates/${id}/`)
  },

  // Campaigns
  getCampaigns: async (params?: {
    status?: string
    page?: number
  }): Promise<PaginatedResponse<NewsletterCampaign>> => {
    const response = await apiClient.get('/newsletter/campaigns/', { params })
    return response.data
  },

  getCampaign: async (id: number): Promise<NewsletterCampaign> => {
    const response = await apiClient.get(`/newsletter/campaigns/${id}/`)
    return response.data
  },

  createCampaign: async (data: Partial<NewsletterCampaign>): Promise<NewsletterCampaign> => {
    const response = await apiClient.post('/newsletter/campaigns/', data)
    return response.data
  },

  updateCampaign: async (id: number, data: Partial<NewsletterCampaign>): Promise<NewsletterCampaign> => {
    const response = await apiClient.patch(`/newsletter/campaigns/${id}/`, data)
    return response.data
  },

  deleteCampaign: async (id: number): Promise<void> => {
    await apiClient.delete(`/newsletter/campaigns/${id}/`)
  },

  prepareCampaign: async (id: number): Promise<{ detail: string; recipients_count: number }> => {
    const response = await apiClient.post(`/newsletter/campaigns/${id}/prepare/`)
    return response.data
  },

  sendCampaign: async (id: number): Promise<{ detail: string; sent_count: number }> => {
    const response = await apiClient.post(`/newsletter/campaigns/${id}/send/`)
    return response.data
  },

  scheduleCampaign: async (id: number, scheduledAt: string): Promise<{ detail: string }> => {
    const response = await apiClient.post(`/newsletter/campaigns/${id}/schedule/`, {
      scheduled_at: scheduledAt,
    })
    return response.data
  },

  cancelCampaign: async (id: number): Promise<{ detail: string }> => {
    const response = await apiClient.post(`/newsletter/campaigns/${id}/cancel/`)
    return response.data
  },

  duplicateCampaign: async (id: number): Promise<NewsletterCampaign> => {
    const response = await apiClient.post(`/newsletter/campaigns/${id}/duplicate/`)
    return response.data
  },

  getCampaignRecipients: async (id: number, params?: {
    status?: string
    page?: number
  }): Promise<PaginatedResponse<NewsletterRecipient>> => {
    const response = await apiClient.get(`/newsletter/campaigns/${id}/recipients/`, { params })
    return response.data
  },

  getCampaignStats: async (): Promise<CampaignStats> => {
    const response = await apiClient.get('/newsletter/campaigns/stats/')
    return response.data
  },

  // Scheduled Digests
  getDigests: async (): Promise<ScheduledDigest[]> => {
    const response = await apiClient.get('/newsletter/digests/')
    return response.data.results || response.data
  },

  getDigest: async (id: number): Promise<ScheduledDigest> => {
    const response = await apiClient.get(`/newsletter/digests/${id}/`)
    return response.data
  },

  createDigest: async (data: Partial<ScheduledDigest>): Promise<ScheduledDigest> => {
    const response = await apiClient.post('/newsletter/digests/', data)
    return response.data
  },

  updateDigest: async (id: number, data: Partial<ScheduledDigest>): Promise<ScheduledDigest> => {
    const response = await apiClient.patch(`/newsletter/digests/${id}/`, data)
    return response.data
  },

  deleteDigest: async (id: number): Promise<void> => {
    await apiClient.delete(`/newsletter/digests/${id}/`)
  },
}
